import { useEffect } from "react";
import { Instagram, Youtube, ExternalLink } from "lucide-react";
import { Grain } from "@/components/site/primitives";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const PAGE_PATH = "/blog/jailer-2-chethan-dsouza-action-direction";
const TITLE = "Jailer 2: Chethan D’Souza’s Action Direction";
const DESCRIPTION =
  "Discover how Chethan D’Souza directed the high-octane action sequences in Rajinikanth’s Jailer 2 — from real live explosions to minimal movement choreography and practical stunt design.";
const PUBLISH_DATE = "2026-10-24";
const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/chethandsouza/";
const INSTAGRAM_REEL_URL =
  "https://www.instagram.com/reel/Deir9f_Bze9/?obrf=MWRvazNhNW9hYnN4bQ==";
const YOUTUBE_WRONG_MOVE_URL = "https://www.youtube.com/watch?v=TJNn_FBjKWs";
const CF_ACTION_URL = "https://www.cfactiondesign.com";

const KEY_PILLARS = [
  {
    title: "Choreography",
    desc: "Planning the movements, reactions and flow of a fight.",
  },
  {
    title: "Characterisation",
    desc: "Ensuring that the action reflects the character’s personality and abilities.",
  },
  {
    title: "Timing",
    desc: "Building anticipation and delivering important moments at the right instant.",
  },
  {
    title: "Coordination",
    desc: "Working with performers, stunt teams, cinematographers and other departments.",
  },
  {
    title: "Safety",
    desc: "Planning and supervising stunts with appropriate professional precautions.",
  },
  {
    title: "Visual Storytelling",
    desc: "Making each movement serve the scene and the overall narrative.",
  },
];

const FAQS = [
  {
    q: "Who is the action director of Jailer 2?",
    a: "Chethan D’Souza is the Action Director and stunt choreographer for Jailer 2, starring Rajinikanth and directed by Nelson Dilipkumar.",
  },
  {
    q: "Was the explosion behind Rajinikanth in the Jailer 2 trailer real?",
    a: "Yes. Chethan D’Souza revealed in interviews that the iconic explosion behind Rajinikanth was executed using a real live blast on set, combined with stringent safety precautions and subtle visual enhancement in post-production.",
  },
  {
    q: "What makes the action design unique in Jailer 2?",
    a: "The action was engineered around Rajinikanth’s charismatic screen presence using minimal, impactful movement and Nelson Dilipkumar’s signature wide, controlled camera framing rather than chaotic cuts.",
  },
  {
    q: "What is Chethan D’Souza’s action design company?",
    a: "Chethan D’Souza founded CF Action Design, an action design and stunt previsualisation company providing fight choreography, 3D animatics, rigging and motion capture for film and gaming productions.",
  },
  {
    q: "What other notable projects has Chethan D’Souza worked on?",
    a: "Chethan has designed stunts and choreographed action for several major Indian titles, including Aavesham, U Turn, Bagheera, UI, Max, 45 and Bhairathi Ranagal.",
  },
];

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const existed = !!el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prevContent = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, existed, prevContent };
}

export default function Jailer2ActionDirectionPost() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const origin = window.location.origin;
    const url = `${origin}${PAGE_PATH}`;
    const image = `${origin}/blog-image-action-direction.jpeg`;

    const metaRecords = [
      upsertMeta("name", "description", DESCRIPTION),
      upsertMeta("name", "robots", "index, follow"),
      upsertMeta("property", "og:type", "article"),
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", url),
      upsertMeta("property", "og:image", image),
      upsertMeta("name", "twitter:card", "summary_large_image"),
      upsertMeta("name", "twitter:title", TITLE),
      upsertMeta("name", "twitter:description", DESCRIPTION),
      upsertMeta("name", "twitter:image", image),
    ];

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalExisted = !!canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevHref = canonical.getAttribute("href");
    canonical.setAttribute("href", url);

    const ldJson = document.createElement("script");
    ldJson.type = "application/ld+json";
    ldJson.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: TITLE,
          description: DESCRIPTION,
          image,
          datePublished: PUBLISH_DATE,
          dateModified: PUBLISH_DATE,
          author: { "@type": "Person", name: "Chethan D’Souza" },
          publisher: {
            "@type": "Organization",
            name: "CF Action Design",
            logo: { "@type": "ImageObject", url: `${origin}/favicon-new.jpeg` },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ldJson);

    return () => {
      document.title = prevTitle;
      metaRecords.forEach(({ el, existed, prevContent }) => {
        if (!existed) el.remove();
        else if (prevContent !== null) el.setAttribute("content", prevContent);
      });
      if (!canonicalExisted) canonical.remove();
      else if (prevHref) canonical.setAttribute("href", prevHref);
      ldJson.remove();
    };
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Grain />
      <article className="px-4 md:px-10 pt-6 md:pt-10 pb-14 md:pb-24">
        <div className="mx-auto max-w-[1600px]">
          {/* Header Image Collage / Banner */}
          <div className="max-w-[1100px] mx-auto">
            <div className="relative w-full aspect-[16/9] overflow-hidden border border-border bg-[#0a0a0a]">
              <img
                src="/blog-image-action-direction.jpeg"
                alt="Jailer 2 Chethan D’Souza Action Direction"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = "/blog-image.png2.jpeg";
                }}
              />
            </div>
          </div>

          {/* Header */}
          <header className="max-w-[820px] mx-auto mt-10 md:mt-14">
            <span className="kicker">CF Action Design · Published Oct 24, 2026</span>
            <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight mt-4">
              {TITLE}
            </h1>
          </header>

          {/* Lead Paragraphs */}
          <div className="max-w-[820px] mx-auto mt-8 md:mt-10 space-y-5">
            <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
              Rajinikanth’s return as Tiger Muthuvel Pandian in <em>Jailer 2</em> has
              brought renewed excitement to fans of Tamil action cinema. With powerful
              screen presence, dramatic confrontations and larger-than-life moments, the
              film highlights how action direction plays an important role in creating
              memorable theatrical experiences.
            </p>
            <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
              Behind these sequences is Chethan D’Souza, the Action Director responsible
              for designing the film’s stunt sequences. His work brings together
              choreography, timing, physical performance and the creative vision of
              director Nelson Dilipkumar to create action moments that complement
              Rajinikanth’s iconic screen style.
            </p>
          </div>

          {/* Content Sections */}
          <div className="max-w-[820px] mx-auto mt-10 md:mt-14">
            {/* Section 1 */}
            <section className="mt-10 md:mt-14 first:mt-0">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Jailer 2: Continuing the Tiger Muthuvel Pandian Story
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Directed by Nelson Dilipkumar, <em>Jailer 2</em> continues the story
                  introduced in the 2023 blockbuster <em>Jailer</em>. Rajinikanth
                  reprises his role as Tiger Muthuvel Pandian, a retired jailer whose
                  quiet exterior hides a formidable presence.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  The sequel brings together an ensemble cast, including Ramya Krishnan,
                  Yogi Babu, Mohanlal, Shiva Rajkumar, Jackie Shroff, Vidya Balan and
                  S. J. Suryah. Anirudh Ravichander returns as the music composer, adding
                  energy to the film’s dramatic and action-driven moments.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  For a film built around a recognisable character and a distinctive
                  cinematic style, action sequences must do more than provide spectacle.
                  They need to suit the character, support the story and deliver the
                  impact audiences expect from a Rajinikanth film.
                </p>
              </div>

              {/* YouTube Callout */}
              <div className="mt-6 p-4 border border-border/80 bg-background/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <Youtube className="text-red-500 shrink-0" size={20} />
                  <span className="font-mono-c text-xs tracking-wider uppercase text-foreground/80">
                    Featured Video: “WRONG MOVE”
                  </span>
                </div>
                <a
                  href={YOUTUBE_WRONG_MOVE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-mono-c text-xs uppercase tracking-wider text-primary hover:underline"
                >
                  Watch on YouTube <ExternalLink size={13} />
                </a>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Chethan D’Souza: The Action Director Behind Jailer 2
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  An Action Director plays a crucial role in planning and executing the
                  physical action seen on screen. This involves designing fights,
                  coordinating stunt performers, developing movement patterns and working
                  closely with the director and other departments to achieve the intended
                  visual impact.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  For <em>Jailer 2</em>, Chethan D’Souza worked on bringing Nelson
                  Dilipkumar’s ideas for action to life. In interviews, he has discussed
                  how the director’s clear vision influenced the choreography, framing and
                  timing of important action moments.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  One particularly interesting aspect of their approach was creating
                  larger-than-life moments through minimal movement. Rather than making
                  every fight unnecessarily complicated, the action was designed around
                  the character and the impact of each movement.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  This approach demonstrates that effective action direction is not simply
                  about the number of stunts in a scene. It is about understanding when to
                  build tension, when to deliver a dramatic moment and how to make every
                  movement count.
                </p>
              </div>

              {/* Instagram Profile Callout */}
              <div className="mt-5">
                <a
                  href={INSTAGRAM_PROFILE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 font-mono-c text-xs tracking-[0.15em] uppercase link-underline"
                >
                  <Instagram size={16} /> Follow on Instagram: @chethandsouza
                </a>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                The Real Explosion Behind Rajinikanth
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  One of the notable talking points surrounding the <em>Jailer 2</em> trailer
                  was the explosion seen behind Rajinikanth. In an interview Chethan D’Souza
                  revealed that the explosion was created using a real live blast, with
                  some enhancements added to the footage.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  The revelation offered audiences a glimpse into the practical effects
                  involved in creating the film’s action spectacle.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  A sequence involving a practical explosion requires detailed planning,
                  coordination between specialist teams and strict safety procedures.
                  Cinematography, editing and post-production can then help shape the final
                  result to match the director’s vision.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  For an Action Director, the challenge is to ensure that such moments
                  work as part of the scene rather than becoming isolated visual effects.
                  The placement of the performer, timing of the action, camera framing and
                  surrounding atmosphere all contribute to the final impact.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  The explosion in <em>Jailer 2</em> illustrates how practical effects and
                  post-production techniques can work together to deliver a dramatic
                  cinematic moment.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Designing Action Around Rajinikanth’s Screen Presence
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Rajinikanth’s style is an important part of his appeal. His gestures,
                  expressions, pauses and movements often become defining moments in his
                  films.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  According to Chethan D’Souza’s interviews, designing action for{" "}
                  <em>Jailer 2</em> involved understanding the character and the kind of
                  impact each sequence needed to create. The goal was to make the action
                  feel powerful without relying on excessive movement.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  The director also had a distinct approach to staging action scenes,
                  favouring controlled framing and carefully timed moments. Chethan
                  explained that working within this style required precision because even
                  small movements become noticeable when the camera remains wide and
                  relatively static.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  This highlights an essential skill in action direction: adapting
                  choreography to the filmmaking style. A technique that works well in a
                  fast-cut fight sequence may not produce the same effect in a longer,
                  uninterrupted shot.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  By aligning movement, framing and timing, the action team can create
                  scenes that feel natural to the character while maintaining their
                  dramatic impact.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Jailer 2 Reviews and Audience Reactions
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Following its theatrical release on October 15, 2026, <em>Jailer 2</em>{" "}
                  generated discussion among fans and film critics. Rajinikanth’s screen
                  presence, the film’s action highlights and Anirudh Ravichander’s
                  background score featured prominently in early reactions.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Some viewers praised the film’s larger-than-life moments and the
                  excitement surrounding the superstar’s return.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Why Action Direction Matters in Indian Cinema
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Action sequences are often among the most anticipated parts of
                  commercial Indian films. Whether a scene involves hand-to-hand combat, a
                  large-scale explosion or a character’s dramatic entrance, its success
                  depends on preparation and execution.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  An experienced Action Director considers several elements when
                  developing a sequence:
                </p>
              </div>

              {/* Pillars Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                {KEY_PILLARS.map((pillar) => (
                  <div
                    key={pillar.title}
                    className="p-4 border border-border/70 bg-secondary/10 flex flex-col justify-between"
                  >
                    <span className="font-mono-c text-xs uppercase tracking-wider text-primary font-semibold mb-1">
                      {pillar.title}
                    </span>
                    <p className="text-sm text-foreground/85 leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                ))}
              </div>

              <p className="text-sm sm:text-base leading-relaxed text-foreground/90 mt-5">
                In <em>Jailer 2</em>, the discussion around Chethan D’Souza’s work
                highlights the importance of combining technical precision with a clear
                creative direction.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                CF Action and the Craft of Action Design
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Through its association with action design, CF Action can use projects
                  such as <em>Jailer 2</em> to highlight the expertise and coordination
                  involved in creating cinematic action.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  The film provides a relevant example of how stunt choreography,
                  practical effects and carefully designed movements contribute to the
                  audience’s experience. It also demonstrates why collaboration between an
                  Action Director and a film director is essential to translating creative
                  ideas into effective on-screen sequences.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Action filmmaking is a specialised discipline that requires both physical
                  understanding and visual storytelling. The objective is not simply to
                  make a scene look intense, but to ensure that the action feels
                  purposeful, controlled and connected to the character.
                </p>
              </div>

              {/* Website Link */}
              <div className="mt-5">
                <a
                  href={CF_ACTION_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-mono-c text-xs uppercase tracking-wider text-primary link-underline"
                >
                  Visit CF Action Design <ExternalLink size={13} />
                </a>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mt-10 md:mt-14">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                Conclusion
              </h2>
              <div className="space-y-4">
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  <em>Jailer 2</em> brings Rajinikanth back as Tiger Muthuvel Pandian,
                  combining his distinctive screen presence with dramatic action and
                  cinematic spectacle.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  Chethan D’Souza’s work as the film’s Action Director offers an insight
                  into the thought and precision behind these moments. From the practical
                  explosion featured in the trailer to the carefully timed movements
                  designed around the character, his approach highlights the importance
                  of choreography, coordination and creative collaboration.
                </p>
                <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                  For CF Action, <em>Jailer 2</em> is an opportunity to spotlight the
                  craft behind action filmmaking and the professionals who help transform
                  a director’s vision into memorable screen moments.
                </p>
              </div>

              {/* Instagram Reel CTA */}
              <div className="mt-8 p-5 border border-border bg-secondary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Instagram size={18} className="text-pink-500" />
                    <span className="font-mono-c text-xs tracking-wider uppercase font-semibold text-foreground">
                      Watch Behind The Scenes
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Check out exclusive action clips and updates from Jailer 2 on Instagram.
                  </p>
                </div>
                <a
                  href={INSTAGRAM_REEL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-border bg-background hover:bg-secondary text-xs font-mono-c uppercase tracking-wider transition-colors shrink-0"
                >
                  View on Instagram <ExternalLink size={13} />
                </a>
              </div>
            </section>

            {/* FAQ Accordion */}
            <section className="mt-16 md:mt-20">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-6">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="border-t border-border">
                {FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-sm sm:text-base font-semibold hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>
        </div>
      </article>
    </div>
  );
}
